import React, { useCallback, useMemo } from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter, NavLink } from "react-router-dom";

import { CreateLocalPostButton } from "./CreateLocalPostButton";
import { InstagramPostItem } from "./InstagramPostItem";
import { PageTitle } from "../../components/Common/PageTitle";
import { LocalPostItem } from "../../components/DashboardComponents/LocalPostsComponent/LocalPostItem";
import { RecentMediaListContext } from "../../components/DashboardComponents/RecentPostsList";
import ExternalIntegrations from "../../components/FacilityDetailsComponents/ExternalIntegrations";
import { InfiniteScroller } from "../../components/InfiniteScroller";
import SchedulePostItem from "../../components/LocalPostsPageComponents/SchedulePostItem";
import { useMe } from "../../hooks/queries";
import { AccountType } from "../../models/AccountData";
import { OrgFeatures } from "../../models/featureUseLimits/FeatureUseLimitsData.const";
import { LocalPostData } from "../../models/LocalPostData";
import { SchedulePostData } from "../../models/SchedulePostData";
import DataSvc from "../../services/dataSvc";
import useLocation from "../FacilityDetailsPage/hooks/useLocation";

import "./styles.scss";

interface MatchParams {
  locationId?: string;
}

type ILocalPostsPageProps = RouteComponentProps<MatchParams>;

const LocalPostsPage: React.FunctionComponent<ILocalPostsPageProps> = (
  props
) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`localPostsPage.${key}`);

  const locationId = props.match.params.locationId ?? "";

  const [scheduledPostsRefresh, setScheduledPostsRefresh] = React.useState<
    (() => void) | null
  >(null);

  const { data: locationData } = useLocation(locationId);

  const { data: me } = useMe();

  const canModifyLocalPost = useMemo(() => {
    if (me?.type === AccountType.ADMIN || me?.type === AccountType.LOC_ADMIN) {
      return true;
    }

    return (
      me?.type === AccountType.ORG_ADMIN &&
      me?.organization?.org_feature_use_limit.some(
        (item) =>
          item.org_feature.name === OrgFeatures.USE_LOCAL_POST && item.can_use
      )
    );
  }, [me?.organization?.org_feature_use_limit, me?.type]);

  const isInstagramIntegrated = useMemo(() => {
    return (
      !!locationData?.locations_instagram_integrations &&
      locationData.locations_instagram_integrations.length > 0
    );
  }, [locationData?.locations_instagram_integrations]);

  React.useEffect(() => {
    if (locationId) {
      ReactGA.event({
        category: "facility_details",
        action: "View All Local Posts"
      });
    }
  }, [locationId]);

  const fetchInstagramPosts = useCallback(
    async (params: any) => {
      const response = await DataSvc.getLocationInstagramPosts(
        locationId,
        params
      );

      return response;
    },
    [locationId]
  );

  const fetchScheduledPosts = useCallback(
    async (param: any) => {
      return DataSvc.getScheduledPosts(locationId, param);
    },
    [locationId]
  );

  return (
    <div className="right-content local-posts-page">
      <div className="back-top-bar">
        <NavLink to={`/location/${locationId}`} className="back-link">
          <i className="icons icon-back"></i>
          {t("back_to_facility_details")}
        </NavLink>
      </div>

      {locationData && (
        <>
          {canModifyLocalPost && (
            <div className="location-name">
              <PageTitle text={locationData.name} />
              <CreateLocalPostButton
                locationData={locationData}
                onSchedulePostSuccess={() => {
                  if (scheduledPostsRefresh) {
                    scheduledPostsRefresh();
                  }
                }}
              />
            </div>
          )}

          <div className="section-title">{t("facility_latest_posts")}</div>
          <div className="group-title">
            {t("gbp")}
            <div className="line"></div>
          </div>
          <div className="recent-local-posts-container">
            <InfiniteScroller
              direction="horizontal"
              pageSize={10}
              dependencyKey={`${locationId}`}
              fetchFunction={DataSvc.getLocalPosts}
              initialParams={{
                order: "desc",
                sort: "post_date",
                seek_total_count: true,
                location_id: [locationId]
              }}
              noItemsComponent={<div>{t("no_posts_available")}</div>}
              renderItem={(item: any, refresh: () => void) => (
                <LocalPostItem
                  key={item.id}
                  localPostItem={item as LocalPostData}
                  isShowLocationName={false}
                  context={RecentMediaListContext.OWN_LOCATION_HISTORY}
                  canModify={canModifyLocalPost}
                  showDeleteButton={true}
                  showEditButton={true}
                  refreshCallback={refresh}
                />
              )}
            />
          </div>

          <div className="group-title">
            {t("instagram")}
            <div className="line"></div>
          </div>
          {isInstagramIntegrated ? (
            <div className="recent-local-posts-container">
              <InfiniteScroller
                direction="horizontal"
                pageSize={10}
                dependencyKey={`${locationId}`}
                fetchFunction={fetchInstagramPosts}
                initialParams={{
                  order: "desc",
                  sort: "timestamp",
                  seek_total_count: true
                }}
                renderItem={(item: any) => (
                  <InstagramPostItem
                    key={item.id}
                    post={item}
                    locationData={locationData}
                    isShowLocationName={false}
                  />
                )}
                noItemsComponent={<div>{t("no_posts_available")}</div>}
              />
            </div>
          ) : (
            <ExternalIntegrations locationData={locationData} />
          )}
          <div className="group-title">
            {t("scheduled_posts")}
            <div className="line"></div>
          </div>
          <div className="schedule-posts-container">
            <InfiniteScroller
              direction="horizontal"
              pageSize={10}
              dependencyKey={`${locationId}`}
              fetchFunction={fetchScheduledPosts}
              initialParams={{
                order: "asc",
                sort: "scheduled_at",
                seek_total_count: true,
                location_id: [locationId]
              }}
              noItemsComponent={<div>{t("no_posts_available")}</div>}
              onRefresh={(refreshFn) =>
                setScheduledPostsRefresh(() => refreshFn)
              }
              renderItem={(item: any, refresh: () => void) => (
                <SchedulePostItem
                  key={item.id}
                  schedulePostItem={item as SchedulePostData}
                  isShowLocationName={false}
                  context={RecentMediaListContext.OWN_LOCATION_HISTORY}
                  canModify={canModifyLocalPost}
                  showEditButton={false}
                  showCancelButton={true}
                  refreshCallback={refresh}
                />
              )}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default withRouter(LocalPostsPage);
